import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { BeaconDTO } from "../Beacon";
import { FacilityDTO, FacilityFloorDTO } from "../../facilities/Facility";
import { TitleService } from "src/app/title.service";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { SessionService } from "src/app/session.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { BeaconService } from "src/app/beacons/beacon.service";
import { AreYouSureDialogComponent } from "src/app/shared/dialogs/are-you-sure-dialog/are-you-sure-dialog.component";
import { union, flatten, filter, find, remove } from "lodash";
import { BeaconRegionDTO } from "src/app/beacon-regions/BeaconRegion";
import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { UiService } from "src/app/ui/ui.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-beacon",
  templateUrl: "./beacon.component.html",
  styleUrls: ["./beacon.component.scss"],
})
export class BeaconComponent implements OnInit {
  newBeacon!: boolean;
  beacon?: BeaconDTO;
  beaconForm!: FormGroup;
  floors?: FacilityFloorDTO[];
  regionSearchText: string = "";
  unassignedRegions!: BeaconRegionDTO[];
  private onComponentDestroy = new Subject();

  constructor(
    private activatedRoute: ActivatedRoute,
    private TitleService: TitleService,
    private dialog: MatDialog,
    private router: Router,
    private SessionService: SessionService,
    private NotificationService: NotificationService,
    private BeaconService: BeaconService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.beacon = this.activatedRoute.snapshot.data.beacon;
    this.floors = flatten(
      union(
        this.activatedRoute.snapshot.data.facilities.records.map(
          (facil: FacilityDTO) => {
            facil.floors?.map((fl) => {
              fl.facility = facil;
              fl.name =  facil.name + ": " + fl.name;
            });
            return facil.floors;
          },
        ),
      ),
    );
    this.unassignedRegions = filter(
      this.activatedRoute.snapshot.data.beaconRegions.records,
      (br) => {
        if (
          this.beacon?.beaconRegions == undefined ||
          this.beacon.beaconRegions.length == 0
        ) {
          return true;
        }
        return (
          find(this.beacon?.beaconRegions, (abr) => {
            return abr.id != br.id;
          }) != undefined
        );
      },
    );
    this.newBeacon = !this.beacon;

    if (!this.newBeacon) {
      this.TitleService.setTitle(
        this.TitleService.getTitle() + ` | ${this.beacon?.name}`,
      );
    }

    this.initForms();
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  initForms() {
    this.beaconForm = new FormGroup({
      name: new FormControl(this.beacon?.name, Validators.required),
      facilityFloor: new FormControl(
        this.beacon?.facility_floor_id,
        Validators.required,
      ),
      note: new FormControl(this.beacon?.note),
      disabled: new FormControl(this.beacon?.disabled),
    });
  }

  get beaconDTO() {
    let beaconDTO = {
      name: this.beaconForm.get("name")?.value,
      facilityFloor: this.beaconForm.get("facilityFloor")?.value,
      facility_floor_id: this.beaconForm.get("facilityFloor")?.value,
      note: this.beaconForm.get("note")?.value,
      disabled: this.beaconForm.get("disabled")?.value || false,
    };

    if (this.beacon) {
      beaconDTO = { ...this.beacon, ...beaconDTO };
    }

    return beaconDTO;
  }

  get saveDisabled() {
    return this.beaconForm.invalid;
  }

  get currentAccount() {
    return this.SessionService.currentAccountValue!;
  }

  saveNewBeacon() {
    this.uiService
      .waitForEvent(
        this.BeaconService.createBeacon(
          this.currentAccount.accountID,
          this.beaconDTO as BeaconDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (saved) => {
          let beac: BeaconDTO = <BeaconDTO>(<unknown>saved);
          this.NotificationService.openSuccessSnackBar(
            "Beacon created successfully",
          );
          this.router.navigate([beac.id], {
            relativeTo: this.activatedRoute.parent,
          });
        },
        () => {
          this.NotificationService.openErrorSnackBar("Error creating beacon");
        },
      );
  }

  saveBeaconUpdates() {
    this.uiService
      .waitForEvent(
        this.BeaconService.updateBeacon(
          this.currentAccount.accountID,
          this.beaconDTO as BeaconDTO,
        ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        () => {
          this.NotificationService.openSuccessSnackBar(
            "Beacon updated successfully",
          );
        },
        () => {
          this.NotificationService.openErrorSnackBar("Error updating beacon");
        },
      );
  }

  cancel() {
    this.router.navigate(["list"], {
      relativeTo: this.activatedRoute.parent!.parent,
    });
  }

  deleteBeacon() {
    this.dialog
      .open(AreYouSureDialogComponent, {
        data: {
          areYouSureInfo: {
            description: `Are you sure you want to delete this beacon ( ${
              this.beacon!.name
            } )?`,
            warning: "Warning: this is not reversible!",
          },
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((result) => {
        if (result) {
          this.uiService
            .waitForEvent(
              this.BeaconService.deleteBeacon(
                this.currentAccount.accountID,
                this.beacon!.id,
              ),
            )
            .pipe(takeUntil(this.onComponentDestroy))
            .subscribe(
              () => {
                this.NotificationService.openSuccessSnackBar("Beacon deleted");

                this.router.navigate(["list"], {
                  relativeTo: this.activatedRoute.parent!.parent,
                });
              },
              () => {
                this.NotificationService.openErrorSnackBar(
                  "Error deleting beacon",
                );
              },
            );
        }
      });
  }

  onDropBeaconRegion(event: CdkDragDrop<any>) {
    // ignore if container has not changed
    if (event.container.data.list === event.previousContainer.data.list) {
      return;
    }

    // if target is unassigned, remove from beacon and add to unassigned (same as removal by click)
    if (event.container.data.list == "unassigned") {
      this.onBeaconRegionRemove(event.item.data);
    }

    // if source was unassigned
    else if (event.container.data.list == "assigned") {
      // remove from unassigned
      remove(this.unassignedRegions, (br) => {
        return br.id == event.item.data.id;
      });
      this.beacon?.beaconRegions?.push(event.item.data);
    }
  }

  onBeaconRegionRemove(region: BeaconRegionDTO) {
    // Remove from assigned
    remove(this.beacon!.beaconRegions!, (br) => {
      return br.id == region.id;
    });
    // Add to unassigned
    this.unassignedRegions.push(region);
  }
}
